// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 초대 수락  `/join`
//
//  흐름은 이렇다.
//    ① 메일의 `https://cv.kr/invite/<43자>` 를 누른다
//    ② 서버가 토큰을 **쿠키(cv_inv)로 옮기고** `/join` 으로 302 한다. DB 를 안 본다
//    ③ 여기(이 화면). 로그인했나?  아니오 → 아래 InviteLogin  /  예 → accept 호출
//
//  ★★★ 이 화면에서 제일 중요한 것은 버튼이 아니라 **한 문장**이다 ★★★
//
//    「초대장이 온 메일 주소로 로그인하세요. 방금 그 메일을 열어 보신 주소입니다.」
//
//    이유: `accept_invite` 는 네 가지 실패를 한 문장으로 뭉갠다(007:174-177) —
//    없는 토큰 · pending 아님 · 만료 · **이메일 불일치**. 그중 이메일 불일치만 사용자가
//    스스로 고칠 수 있는데, 서버는 대상 주소를 **알려줄 수 없다**(`invite_select` 가
//    `member.view` 를 요구해서 초대받은 당사자가 자기 초대장 행을 못 읽는다 — 004:130).
//
//    그래서 마스킹 RPC 를 새로 만들 뻔했는데, 그럴 필요가 없다.
//    **정답 주소는 이미 그 사람 폰에 열려 있다** — 초대 메일이 바로 그 주소로 배달됐다.
//    화면이 그 사실을 가리켜 주면 된다. DB 비용 0, RPC 0.
//
//  ⚠️ 이 화면이 없으면 감리는 이렇게 된다(실제 시나리오):
//     09:12 링크 탭 → 로그인 화면(초대 얘기 없음) → 기억나는 개인 Gmail 을 넣음 →
//     09:19 코드 도착 → 09:20 로그인 성공 → **수락 실패** → 막다른 화면.
//     8분과 8자리 코드 하나를 태우고, 어느 주소가 맞는지는 여전히 모른다. 전화한다.
//     안 받으면 안 쓴다.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Box, Button, Card, Loader, Stack, Text, Title } from '@mantine/core';
import { ApiError, humanMessage } from '../api/client';
import { useSession } from '../auth/SessionProvider';
import { Contact } from '../components/Contact';
// ★ raw <Alert> 를 쓰지 마라 — `color="red"` 는 theme.ts 에서 모르는 이름이라
//   **초록 안내 상자**로 떨어졌다. Notice 는 tone 을 타입으로 강제한다.
import { Notice } from '../components/Notice';
import { CenteredShell } from '../components/Shell';
import { navigate } from '../router';
import { Login } from './Login';
import { acceptInvite } from './admin/api';

type Phase =
  | { s: 'working' }
  /** 쿠키가 없다. 링크를 다시 누르면 **완전히 복구된다.** */
  | { s: 'no_cookie' }
  /** 수락이 거부됐다. 사유는 서버가 구분해 주지 않는다(하나만 예외 — inviter) */
  | { s: 'refused'; inviter: boolean }
  | { s: 'error'; error: ApiError };

export function Invite() {
  const { state, reload, signOut } = useSession();
  const [phase, setPhase] = useState<Phase>({ s: 'working' });

  useEffect(() => {
    if (state.status !== 'authed') return;
    let alive = true;
    void (async () => {
      try {
        const { slug } = await acceptInvite();
        // 세션의 현장 목록을 새로 읽고 그 현장으로 보낸다.
        await reload();
        if (alive) navigate(`/${slug}`, { replace: true });
      } catch (e) {
        if (!alive) return;
        if (e instanceof ApiError && e.code === 'no_invite_cookie') {
          setPhase({ s: 'no_cookie' });
        } else if (e instanceof ApiError && e.code === 'inviter_lost_permission') {
          setPhase({ s: 'refused', inviter: true });
        } else if (e instanceof ApiError && e.code === 'invite_unusable') {
          setPhase({ s: 'refused', inviter: false });
        } else {
          setPhase({ s: 'error', error: e instanceof ApiError ? e : new ApiError('server', 0, null, null) });
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [state.status, reload]);

  // ── 로그인 안 됨 ──────────────────────────────────────────────────────
  if (state.status === 'anon') return <InviteLogin />;
  if (state.status === 'loading') return <Waiting />;
  if (state.status === 'error') {
    return (
      <CenteredShell>
        <Stack gap="lg" mt="xl">
          <Title order={2}>서버에 연결하지 못했습니다</Title>
          <Text>{humanMessage(state.error)}</Text>
          <Text style={{ color: 'var(--cv-ink-2)' }}>
            초대는 그대로 살아 있습니다. 연결이 돌아오면 링크를 다시 눌러 주세요.
          </Text>
          <Contact prefix="계속 안 되면 연락 주세요" />
        </Stack>
      </CenteredShell>
    );
  }

  // ── 로그인됨 ─────────────────────────────────────────────────────────
  if (phase.s === 'working') return <Waiting />;

  if (phase.s === 'no_cookie') {
    // ⚠️ 이 화면이 없으면 「정의되지 않은 상태」가 된다. 실제 경로:
    //    링크 탭 → 레미콘 도착 → 현장 나감 → 세 시간 뒤 탭 복원 → /join.
    //    쿠키가 없어 빈 화면이 뜨면 그 사람은 **초대가 만료된 줄 안다**(실제로는 14일 남았다).
    return (
      <CenteredShell>
        <Stack gap="lg" mt="xl">
          <Card withBorder padding="lg">
            <Stack gap="sm">
              <Title order={2}>초대 확인 정보가 없습니다.</Title>
              <Text size="md" fw={700}>
                메일의 링크를 한 번 더 눌러 주세요.
              </Text>
              <Text size="md">초대장 자체는 아직 살아 있습니다.</Text>
            </Stack>
          </Card>
          <Button variant="default" size="md" onClick={() => navigate('/')}>
            내 현장 목록으로
          </Button>
          <Contact prefix="링크를 못 찾으시겠다면" />
        </Stack>
      </CenteredShell>
    );
  }

  if (phase.s === 'error') {
    return (
      <CenteredShell>
        <Stack gap="lg" mt="xl">
          {/* 연결 실패는 warn(다시 하면 된다) · 그 밖은 danger. ErrorState.tsx 와 같은 기준.
              ⚠️ 초대 화면에서 이 구분이 특히 중요하다 — 신호가 나쁜 것뿐인데 「이 초대는
                 못 씁니다」로 읽히면 그 사람은 링크를 버리고 다시 안 온다. */}
          <Notice
            tone={
              phase.error.kind === 'offline' || phase.error.kind === 'unavailable'
                ? 'warn'
                : 'danger'
            }
          >
            {humanMessage(phase.error)}
          </Notice>
          <Contact prefix="계속 안 되면 연락 주세요" />
        </Stack>
      </CenteredShell>
    );
  }

  // ── 거부됨 ───────────────────────────────────────────────────────────
  const email = state.user.email;
  return (
    <CenteredShell>
      <Stack gap="lg" mt="xl">
        <Card withBorder padding="lg">
          <Stack gap="md">
            <Title order={2}>이 초대를 열 수 없습니다.</Title>

            {phase.inviter ? (
              // ★ 서버가 유일하게 구분해 주는 사유다(007:180). 다음 행동이 다르다 —
              //   초대한 사람이 아니라 **관리자**에게 요청해야 한다.
              <Stack gap="sm">
                <Text size="md" fw={700}>
                  초대하신 분의 권한이 만료되었습니다.
                </Text>
                <Text size="md">
                  그분이 이 현장에서 빠졌거나 역할이 바뀌었습니다. <b>관리자에게</b> 다시
                  요청해 주세요.
                </Text>
              </Stack>
            ) : (
              <Stack gap="sm">
                <Text size="md">
                  지금 로그인한 주소는 <span className="cv-mono">{email}</span> 입니다.
                </Text>
                <Text size="md" fw={700}>
                  이 중 하나입니다:
                </Text>
                <Stack gap={4}>
                  <Text size="md">
                    · <b>초대장이 다른 주소로 갔습니다.</b> 초대 메일 맨 위에 「이 초대는 ○○○
                    로 보냈습니다」가 적혀 있습니다 — 그 주소로 로그인해 주세요.
                  </Text>
                  <Text size="md">· 초대가 만료되었습니다 (보통 14일)</Text>
                  <Text size="md">· 초대가 취소되었습니다</Text>
                </Stack>
                <Text size="md">어느 쪽이든 초대해 주신 분께 다시 요청하시면 됩니다.</Text>
              </Stack>
            )}
          </Stack>
        </Card>

        {/* ★★ 「다른 주소로 로그인」이 이 화면의 핵심이다. 사용자는 자기가 어떤 주소들을
            갖고 있는지 안다. 대상 주소를 못 보여 줘도, **지금 주소를 보여 주고 갈아탈
            길을 주면 스스로 푼다.** cv_inv 를 cv_s 와 분리해 뒀기 때문에 로그아웃해도
            초대는 그대로 이어진다. */}
        <Stack gap="xs">
          <Button size="md" onClick={() => void signOut()}>
            다른 주소로 로그인
          </Button>
          <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
            로그아웃해도 이 초대는 그대로 유지됩니다.
          </Text>
        </Stack>

        <Button variant="subtle" size="md" onClick={() => navigate('/')}>
          내 현장 목록으로
        </Button>

        <Contact prefix="문의" />
      </Stack>
    </CenteredShell>
  );
}

// ═══ 로그인 안 된 상태 ════════════════════════════════════════════════════

/**
 * 초대 전용 로그인.
 *
 * ★ **일반 로그인 화면을 그대로 재사용하지 않는다.** 그 화면에는 「당진 고속도로」라는
 *   글자도, 「어느 주소로 로그인해야 하는지」도 없다. 감리는 자기가 방금 누른 링크가
 *   통했는지조차 모르고, 주소를 서너 개 가진 채 기억나는 것을 넣는다.
 *
 * ⚠️ 현장 이름은 여기서 못 보여 준다 — 초대받은 사람은 아직 그 현장을 읽을 권한이 없다.
 *    (그래서 「초대를 받으셨습니다」까지만 말한다. 없는 정보를 지어내지 않는다.)
 */
function InviteLogin() {
  return (
    <>
      <Box style={{ maxWidth: 460, margin: '0 auto', padding: '24px 16px 0' }}>
        <Card withBorder padding="lg">
          <Stack gap="xs">
            <Title order={2}>초대를 받으셨습니다</Title>
            <Text size="md">들어가려면 로그인이 필요합니다.</Text>
            <Text size="md" fw={700}>
              초대장이 온 메일 주소로 로그인하세요.
            </Text>
            <Text size="md">방금 그 메일을 열어 보신 주소입니다.</Text>
            <Text size="sm" mt={4} style={{ color: 'var(--cv-ink-2)' }}>
              주소가 기억나지 않으면 초대 메일 맨 위의 「이 초대는 ○○○ 로 보냈습니다」를
              보세요.
            </Text>
          </Stack>
        </Card>
      </Box>
      {/* 아래는 평소의 로그인 화면 그대로다. 코드 입력·재발송·연락처가 전부 거기 있다. */}
      <Login />
    </>
  );
}

// ═══ 안전망 ═══════════════════════════════════════════════════════════════

/**
 * `/invite/{토큰}` 이 **SPA 로 떨어졌을 때** 서버로 되돌려 보낸다.
 *
 * ★ 정상 흐름에서는 이 컴포넌트가 **절대 안 뜬다** — 서버가 302 로 먼저 채간다.
 *   뜬다면 그 경로가 API 까지 못 갔다는 뜻이다(dev 프록시 누락·배포 라우팅 오설정).
 *
 * ⚠️ 이 안전망이 왜 필요한가 [2026-08-11 실제 사고]
 *   초대 메일을 받은 분이 링크를 눌렀는데 **「초대 확인 정보가 없습니다」** 만 봤다.
 *   초대장·토큰·만료·권한이 전부 멀쩡했는데도 그랬다. `/invite` 가 vite 프록시
 *   목록에 없어서 vite 가 200+index.html 을 줬고, 쿠키를 심는 서버 코드가 한 번도
 *   안 불렸기 때문이다. 화면 어디에도 진짜 원인이 안 나왔다.
 *   → 프록시는 고쳤다(`vite.config.ts`). 이건 **같은 사고가 다시 나도 사용자는
 *     아무것도 못 느끼게** 하는 두 번째 그물이다.
 *
 * ★ 무한 루프가 안 난다: 보내는 곳이 `/api/invite/…` 라 `path.startsWith('/invite/')`
 *   에 다시 안 걸린다. 서버는 그 접두사도 같은 핸들러에 걸어 뒀다(index.ts:108).
 */
export function InviteBounce({ path }: { path: string }) {
  useEffect(() => {
    const token = path.slice('/invite/'.length);
    // 서버와 **같은 규격**이다(admin.ts:2227). 이상한 값이면 서버를 두드리지 않는다.
    if (!/^[A-Za-z0-9_-]{20,64}$/.test(token)) {
      navigate('/join', { replace: true });
      return;
    }
    // ⚠️ `navigate` 가 아니라 **진짜 이동**이어야 한다 — 서버가 쿠키를 심어야 하므로
    //    브라우저가 실제로 그 주소를 받아 와야 한다.
    window.location.replace(`/api/invite/${encodeURIComponent(token)}`);
  }, [path]);
  return <Waiting />;
}

function Waiting() {
  return (
    <CenteredShell>
      <Stack align="center" gap="sm" mt="xl" className="cv-delayed">
        <Loader color="cv" />
        <Text size="md">초대를 확인하는 중입니다</Text>
      </Stack>
    </CenteredShell>
  );
}
